"use client";

import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../../lib";

const HEX_TILE_LIGHT = "/images/hex-tile-white.png";
const HEX_TILE_DARK = "/images/hex-tile.png";
const CROSSFADE_FALLBACK_MS = 1500;

interface HexagonMapBackdropProps {
	backdropJpg: string;
}

interface BackdropState {
	active: 0 | 1;
	layers: [string, string];
	/** Incoming layer — active stays on outgoing until flip commits */
	pendingActive: 0 | 1 | null;
}

interface BackdropBiomeImageProps {
	src: string;
	visible: boolean;
	onReady?: () => void;
}

function BackdropBiomeImage({
	src,
	visible,
	onReady,
}: BackdropBiomeImageProps) {
	const hasNotifiedReadyRef = useRef(false);

	useEffect(() => {
		hasNotifiedReadyRef.current = false;
	}, [src]);

	const notifyReady = useCallback(() => {
		if (hasNotifiedReadyRef.current) {
			return;
		}
		hasNotifiedReadyRef.current = true;
		onReady?.();
	}, [onReady]);

	return (
		<div
			className={cn(
				"absolute inset-0 transition-opacity duration-500 ease-in-out motion-reduce:transition-none",
				visible
					? "z-[1] opacity-100"
					: "pointer-events-none z-0 opacity-0",
			)}
			aria-hidden
		>
			<Image
				src={src}
				alt=""
				fill
				className="object-cover"
				priority
				sizes="100vw"
				onLoad={notifyReady}
			/>
		</div>
	);
}

/**
 * Full-bleed biome + hextile wash — matches solution demo / `HexagonBackground` stack.
 * Biome swaps cross-fade between two image layers.
 */
export function HexagonMapBackdrop({ backdropJpg }: HexagonMapBackdropProps) {
	const [backdrop, setBackdrop] = useState<BackdropState>(() => ({
		active: 0,
		layers: [backdropJpg, backdropJpg],
		pendingActive: null,
	}));
	const flipFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);

	const clearFlipFallback = useCallback(() => {
		if (flipFallbackTimerRef.current) {
			clearTimeout(flipFallbackTimerRef.current);
			flipFallbackTimerRef.current = null;
		}
	}, []);

	const commitFlip = useCallback(() => {
		setBackdrop((current) => {
			if (current.pendingActive == null) {
				return current;
			}
			return {
				active: current.pendingActive,
				layers: current.layers,
				pendingActive: null,
			};
		});
	}, []);

	/** Ensure the incoming layer paints at opacity 0 before we flip active. */
	const commitFlipAfterPaint = useCallback(() => {
		requestAnimationFrame(() => {
			requestAnimationFrame(commitFlip);
		});
	}, [commitFlip]);

	const handleIncomingReady = useCallback(() => {
		clearFlipFallback();
		commitFlipAfterPaint();
	}, [clearFlipFallback, commitFlipAfterPaint]);

	useEffect(() => {
		let beganTransition = false;

		setBackdrop((current) => {
			const activeSrc = current.layers[current.active];
			if (activeSrc === backdropJpg) {
				return current;
			}
			beganTransition = true;
			const inactive = current.active === 0 ? 1 : 0;
			const layers: [string, string] =
				inactive === 0
					? [backdropJpg, current.layers[1]]
					: [current.layers[0], backdropJpg];
			return {
				active: current.active,
				layers,
				pendingActive: inactive,
			};
		});

		if (!beganTransition) {
			return;
		}

		clearFlipFallback();
		flipFallbackTimerRef.current = setTimeout(() => {
			flipFallbackTimerRef.current = null;
			commitFlipAfterPaint();
		}, CROSSFADE_FALLBACK_MS);

		return clearFlipFallback;
	}, [backdropJpg, clearFlipFallback, commitFlipAfterPaint]);

	const { active, layers, pendingActive } = backdrop;
	const sameLayers = layers[0] === layers[1];

	return (
		<div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
			<div className="absolute inset-0">
				{sameLayers && pendingActive == null ? (
					<BackdropBiomeImage src={layers[0]} visible />
				) : (
					<>
						<BackdropBiomeImage
							src={layers[0]}
							visible={active === 0}
							onReady={
								pendingActive === 0
									? handleIncomingReady
									: undefined
							}
						/>
						<BackdropBiomeImage
							src={layers[1]}
							visible={active === 1}
							onReady={
								pendingActive === 1
									? handleIncomingReady
									: undefined
							}
						/>
					</>
				)}
			</div>
			<div
				className="absolute inset-0 z-[2] bg-repeat bg-[length:6px] opacity-[0.12] dark:hidden"
				style={{
					backgroundImage: `url(${HEX_TILE_LIGHT})`,
				}}
			/>
			<div
				className="absolute inset-0 z-[2] hidden bg-repeat bg-[length:6px] opacity-[0.06] dark:block"
				style={{
					backgroundImage: `url(${HEX_TILE_DARK})`,
				}}
			/>
			<div className="absolute inset-0 z-[2] bg-gradient-to-b from-black/15 via-transparent to-black/25" />
		</div>
	);
}
